import type {CheckoutRequest, CreditPackCheckoutRequest} from '@Components/checkout-flow/checkout-flow.types';
import {BUY_CREDITS_REQUEST_TRIAL_CONVERSION_SOURCE, CheckoutMode, PreflightOutcome} from '@Components/checkout-flow/checkout-flow.types';
import {canCurrentUserBuyCredits} from '@Components/checkout-flow/checkout-flow-thunk';
import {noop} from '@Utils/general.util';

const creditPackRequestsClearedToOpen = new WeakSet<CheckoutRequest>();
let isCreditPackEligibilityPending = false;

/**
 * Only team admins may buy credit packs, so a team member is turned away before any checkout surface appears,
 * rather than watching the checkout open and then close on them. While the check is in flight a repeat click
 * is dropped, since no modal is up yet to absorb it.
 */
export const startCheckoutForEligibleBuyer = async (request: CheckoutRequest, startCheckout: (request: CheckoutRequest) => void): Promise<void> => {
  if (!isCreditPackPurchaseRequest(request)) {
    startCheckout(request);
    return;
  }
  if (isCreditPackEligibilityPending) {
    return;
  }

  let canBuyCredits;
  isCreditPackEligibilityPending = true;
  try {
    canBuyCredits = await canCurrentUserBuyCredits();
  } finally {
    isCreditPackEligibilityPending = false;
  }

  if (!canBuyCredits) {
    handleIneligibleBuyer(request);
    return;
  }
  creditPackRequestsClearedToOpen.add(request);
  startCheckout(request);
};

/**
 * The checkout flow's backstop for a credit-pack request that reached it without passing through
 * `startCheckoutForEligibleBuyer`. A request the gate already cleared proceeds without a second eligibility request.
 */
export const runCreditPackPreflight = async (request: CheckoutRequest): Promise<PreflightOutcome> => {
  if (!isCreditPackPurchaseRequest(request) || creditPackRequestsClearedToOpen.has(request)) {
    return PreflightOutcome.PROCEED;
  }
  if (await canCurrentUserBuyCredits()) {
    return PreflightOutcome.PROCEED;
  }
  handleIneligibleBuyer(request);
  return PreflightOutcome.DISMISS;
};

export const divertCreditPackPurchaseToTeamAdmin = (onBeforeDivert: () => void = noop): void => {
  onBeforeDivert();
  window.PMW.premiumDialogs.openRequestTrialConversionDialog(BUY_CREDITS_REQUEST_TRIAL_CONVERSION_SOURCE);
};

const handleIneligibleBuyer = (request: CreditPackCheckoutRequest): void => {
  if (request.onIneligibleBuyer !== undefined) {
    request.onIneligibleBuyer();
    return;
  }
  divertCreditPackPurchaseToTeamAdmin(request.onOpen);
};

const isCreditPackPurchaseRequest = (request: CheckoutRequest): request is CreditPackCheckoutRequest => {
  return request.mode === CheckoutMode.CREDIT_PACK && !request.isDesignSystemPreview;
};
